import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/seo/SEO';
import { getProgrammaticVerticalBySlug } from '@/lib/programmaticData';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const TERM_OPTIONS = [3, 6, 12];

const IndustryCalculatorPage: React.FC = () => {
  const { slug = '' } = useParams();
  const vertical = getProgrammaticVerticalBySlug(slug);
  const [ticket, setTicket] = useState(vertical?.avgTicketUsd || 2500);
  const [deposit, setDeposit] = useState(Math.round((vertical?.avgTicketUsd || 2500) * 0.1));
  const [term, setTerm] = useState(6);

  if (!vertical) {
    return (
      <div className="min-h-screen bg-[#f8f5ef] px-6 pb-24 pt-28 text-slate-900 md:pt-32">
        <div className="container mx-auto max-w-4xl rounded-3xl border border-slate-200 bg-white p-8">
          <h1 className="font-sans text-3xl text-slate-900">Calculator not found</h1>
          <p className="mt-3 text-slate-600">Browse available industry calculators.</p>
          <Button asChild className="mt-6 rounded-full bg-slate-900 text-white hover:bg-slate-800">
            <Link to="/calculators">Go to calculator hub</Link>
          </Button>
        </div>
      </div>
    );
  }

  const financedAmount = Math.max(ticket - deposit, 0);
  const monthlyEstimate = useMemo(() => Math.round(financedAmount / term), [financedAmount, term]);
  const currentRevenue = useMemo(() => Math.round(ticket * 20 * 0.3), [ticket]);
  const projectedRevenue = useMemo(
    () => Math.round(currentRevenue * (1 + vertical.benchmarkLiftPercent / 100)),
    [currentRevenue, vertical.benchmarkLiftPercent]
  );
  const projectedLift = projectedRevenue - currentRevenue;

  const appUrl = import.meta.env.VITE_APP_URL || '';
  const appSignUpUrl = appUrl ? `${appUrl}/auth?mode=signup` : '/auth?mode=signup';

  const bluf = `${vertical.industry} payment plan calculator: model monthly options, estimate buyer affordability, and project possible sales increase from financing-enabled checkout in one view.`;

  return (
    <div className="min-h-screen bg-[#f8f5ef] pb-24 text-slate-900">
      <SEO
        title={`${vertical.industry} Payment Plan Calculator`}
        description={bluf}
        canonical={`/calculators/${vertical.slug}`}
      />

      <section className="px-6 pb-12 pt-28 md:pt-32">
        <div className="container mx-auto max-w-6xl rounded-[2rem] border border-slate-800 bg-slate-950 p-8 text-white md:p-10">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-200">{vertical.industry}</p>
          <h1 className="mt-3 max-w-4xl font-sans text-3xl text-white md:text-5xl">{vertical.industry} payment plan calculator</h1>
          <p className="mt-4 max-w-4xl text-slate-200">{bluf}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild className="rounded-full bg-white text-slate-900 hover:bg-slate-100">
              <a href={appSignUpUrl}>
                Start selling
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-slate-600 bg-transparent text-slate-100 hover:bg-slate-800">
              <Link to={`/financing/${vertical.slug}`}>View industry playbook</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="container mx-auto grid max-w-6xl gap-6 md:grid-cols-[1.1fr_0.9fr]">
          <Card className="rounded-3xl border border-slate-200 bg-white">
            <CardContent className="p-6 space-y-5">
              <div>
                <div className="flex items-center justify-between text-sm text-slate-700">
                  <span>Quote amount</span>
                  <strong className="font-semibold text-slate-900">{currency.format(ticket)}</strong>
                </div>
                <input
                  type="range"
                  min={500}
                  max={20000}
                  step={100}
                  value={ticket}
                  onChange={(e) => setTicket(Number(e.target.value))}
                  className="mt-2 w-full accent-slate-900"
                />
              </div>

              <div>
                <div className="flex items-center justify-between text-sm text-slate-700">
                  <span>Deposit amount</span>
                  <strong className="font-semibold text-slate-900">{currency.format(deposit)}</strong>
                </div>
                <input
                  type="range"
                  min={0}
                  max={ticket}
                  step={50}
                  value={deposit}
                  onChange={(e) => setDeposit(Number(e.target.value))}
                  className="mt-2 w-full accent-slate-900"
                />
              </div>

              <div>
                <p className="text-sm text-slate-700">Term</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {TERM_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setTerm(option)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                        term === option
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-300 bg-white text-slate-700 hover:border-slate-500'
                      }`}
                    >
                      {option} months
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-3xl border border-slate-200 bg-white">
            <CardContent className="p-6">
              <h2 className="font-sans text-2xl text-slate-900">Estimated buyer and revenue outcomes</h2>
              <div className="mt-4 space-y-3">
                <p className="text-sm text-slate-500">Financed amount</p>
                <p className="font-sans text-3xl text-slate-900">{currency.format(financedAmount)}</p>
              </div>
              <div className="mt-5 space-y-3">
                <p className="text-sm text-slate-500">Estimated monthly option</p>
                <p className="font-sans text-3xl text-slate-900">{currency.format(monthlyEstimate)}/mo</p>
              </div>
              <div className="mt-5 space-y-3">
                <p className="text-sm text-slate-500">Projected monthly sales upside using a +{vertical.benchmarkLiftPercent}% typical increase</p>
                <p className="font-sans text-2xl text-emerald-700">+{currency.format(projectedLift)}</p>
                <p className="text-xs text-slate-500">Example baseline assumes 20 quotes/month with a 30% close rate. Update with your own numbers for planning.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default IndustryCalculatorPage;
